'use client';

import { type FC, type ReactNode, useMemo } from 'react';
import { ScreenContent } from '../../../screen';
import {
  isPartySnapshot, isOpenSnapshot, isBuyingSnapshot, isPlayingSnapshot, isOverSnapshot, isRoundSnapshot,
} from '@cs-shot/battle-sim';
import { LOADING_PHRASES } from './RushMid.translations';
import { RushMidLiveBattle } from './RushMidLiveBattle';
import { GamesHomeScreen } from './GamesHomeScreen';
import { FighterTab } from './FighterTab';
import { BuyScreen } from './BuyScreen';
import { ResultScreen } from './ResultScreen';
import { RushMidLoadingScreen } from './RushMidLoadingScreen';
import { RushMidErrorScreen } from './RushMidErrorScreen';
import { OpenGameScreen, type OpenGameView } from './OpenGameScreen';
import { HistoryScreen } from './HistoryScreen';
import { RatingScreen } from './RatingScreen';
import { RaffleTab } from '../raffle/RaffleTab';
import { RushMidPartyProvider, type RushMidPartyValue } from './RushMidPartyContext';
import { snapshotToRoundUpdate } from './useRushMidMatch';
import { useRushMidController } from './useRushMidController';
import { useRushMidConfig } from '../../../queries/useRushMidConfig';
import { useProfile } from '../../../queries/useProfileQueries';
import { useGameShare } from '../../../hooks/useGameShare';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './RushMid.translations';

export type { RushMidTab } from './useRushMidController';

export const RushMidGame: FC<{ className?: string; initialTab?: string; initialCreate?: boolean; initialTicket?: number }> = ({ initialTab, initialCreate, initialTicket }) => {
  const cfg = useRushMidConfig();
  const tGate = useLocalTranslation(translations);
  const c = useRushMidController(initialTab, initialCreate);
  const profileId = useProfile().data?.id;
  const invite = useGameShare(c.matchId, tGate.t('gameShareText'), tGate.t('gameShareButton'));
  const partyValue = useMemo<RushMidPartyValue | null>(() => {
    const s = c.snapshot;
    if (!s || !isPartySnapshot(s)) return null;
    return {
      code: s.code,
      isLeader: s.isLeader,
      leaderUserId: s.leaderUserId,
      youUserId: s.youUserId,
      members: s.members,
      mode: s.mode,
      ticket: s.ticket,
      side: s.side,
      matchMode: s.matchMode,
      allowedModes: s.allowedModes,
      onConfig: c.partyConfig,
      onReady: c.partyReady,
      onLeave: c.partyLeave,
      onKick: c.partyKick,
      onShare: c.partyShare.share,
      sharing: c.partyShare.isSharing,
    };
  }, [c.snapshot, c.partyConfig, c.partyReady, c.partyLeave, c.partyKick, c.partyShare.share, c.partyShare.isSharing]);
  if (cfg.comingSoon) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
        <span className="rounded-full border border-side-t/40 bg-side-t/10 px-4 py-1 text-sm font-bold text-side-t">
          {tGate.t('comingSoonBadge')}
        </span>
        <div className="text-3xl font-extrabold text-white">???</div>
        <div className="text-sm text-label-secondary">{tGate.t('comingSoonHint')}</div>
      </div>
    );
  }
  const { t, lang, snapshot, matchId, finished } = c;

  const screen = (): ReactNode => {
    if (!c.assetsReady) {
      const phrases = LOADING_PHRASES[lang];
      const phrase = c.loadError ? t('loadingGame') : phrases[c.phraseIdx % phrases.length];
      return <RushMidLoadingScreen phrase={phrase} pct={Math.round(c.loadProgress * 100)} error={c.loadError ? t('errorTitle') : null} />;
    }

    // WHY: живой бой выше экрана ошибки — чужая ошибка команды не должна размонтировать бой: «в лобби» сбросит матч и ставку
    if (snapshot && isRoundSnapshot(snapshot) && (isPlayingSnapshot(snapshot) || !finished)) {
      return (
        <ScreenContent hideLegalSection scrollKey="rush-mid-battle">
          <div className="absolute inset-0 bg-black">

            <RushMidLiveBattle key={matchId} update={snapshotToRoundUpdate(snapshot)} onAction={c.onAction} onFinished={c.finish} />
          </div>
        </ScreenContent>
      );
    }

    if (c.runtimeError) {
      return (
        <RushMidErrorScreen
          message={c.runtimeError}
          onBack={c.again}
          onTopUp={c.runtimeErrorCode === 'INSUFFICIENT_RESOURCES' ? c.topUp : undefined}
        />
      );
    }

    if (snapshot && isOpenSnapshot(snapshot)) {
      const view: OpenGameView = {
        matchId: snapshot.matchId,
        ticket: snapshot.ticket,
        mode: snapshot.mode,
        map: snapshot.map,
        seats: snapshot.seats,
        botFillAt: snapshot.botFillAt,
        yourSide: snapshot.yourSide,
      };
      const isMember = snapshot.partyRole === 'member';
      return (
        <OpenGameScreen
          game={view}
          youUserId={profileId}
          onLeave={isMember ? c.partyLeave : c.leaveGame}
          leaveVariant={isMember ? 'party' : 'game'}
          onInvite={invite.share}
          inviting={invite.isSharing}
        />
      );
    }

    if (snapshot && isOverSnapshot(snapshot) && snapshot.payout) {
      return (
        <ResultScreen
          payout={snapshot.payout}
          scoreboard={snapshot.scoreboard}
          youFighterId={snapshot.yourFighterId}
          matchId={matchId}
          onAgain={c.again}
          againBusy={c.leavingResult}
          celebrateDrops
          onViewLog={matchId && snapshot.matchMode !== 'bots' ? () => c.openMatch(matchId, 'log') : undefined}
        />
      );
    }

    if (snapshot && isBuyingSnapshot(snapshot)) {
      return (
        <BuyScreen
          key={snapshot.matchId}
          side={snapshot.side}
          teammates={snapshot.allies}
          enemies={snapshot.enemies}
          buyDeadline={snapshot.buyDeadline}
          serverNow={snapshot.serverNow}
          savedLoadout={snapshot.yourLoadout}
          savedReady={snapshot.yourReady}
          onReady={c.ready}
          onSave={c.saveLoadout}
        />
      );
    }

    if (c.tab === 'fighter') return <FighterTab tab={c.tab} onTab={c.switchTab} />;
    if (c.tab === 'history') return <HistoryScreen tab="history" onTab={c.switchTab} onOpen={c.openMatch} />;
    if (c.tab === 'rating') return <RatingScreen tab="rating" onTab={c.switchTab} />;
    if (c.tab === 'raffle') return <RaffleTab tab={c.tab} onTab={c.switchTab} onRequestCreate={c.requestCreate} />;

    return (
      <RushMidPartyProvider value={partyValue}>
        <GamesHomeScreen
          tab={c.tab}
          onTab={c.switchTab}
          onOpen={c.viewGame}
          onSpectate={c.openSpectate}
          onCreate={c.create}
          onCreateParty={c.createParty}
          onJoinParty={c.joinPartyByCode}
          pendingCreate={c.pendingCreate}
          onConsumePendingCreate={c.consumePendingCreate}
          createTicket={initialTicket}
        />
      </RushMidPartyProvider>
    );
  };

  return <>{screen()}</>;
};

export default RushMidGame;
